/**
 * OCHAT: turns upstream's sidebar tree into Ochat's, as a transformation applied
 * AFTER the tree is built.
 *
 * WHY THIS EXISTS
 * Ochat used to edit Sidebar.vue in place: it wrapped whole blocks of the
 * menuItems array in `...(isMenuItemVisible('x') ? [ ... ] : [])`, deleted the
 * Reports block outright, and rewrote ten icon strings scattered through it. The
 * wrapping re-indented hundreds of lines, so git could not line them up against
 * the 259 lines upstream changed in the same file, and the "merge" was really a
 * manual rewrite -- every single release.
 *
 * Here the array stays exactly as upstream wrote it and the Ochat opinions are
 * expressed as data: a set of names reserved for the super admin, a map of icons
 * to swap, and two items to insert. Sidebar.vue keeps one call instead of 751
 * changed lines.
 *
 * Visibility depends on the viewer: super admins get the full upstream menu,
 * agents and administrators get the trimmed one. The list of reserved sections
 * lives in config/reservedSections.js and is shared with the route guard, so the
 * menu and the URLs cannot disagree.
 *
 * ORDER MATTERS: insert, then hide. Inserted items are anchored to upstream
 * items, and one of those anchors (Campaigns) is itself hidden. Hiding first
 * would remove the anchor and the insert would silently fall back to appending.
 */
import { isMenuItemVisible } from '../config/menuVisibility';
import { SUPER_ADMIN_PERMISSION } from '../config/permissions';

/**
 * Ochat's icons for upstream entries. Applied only where upstream already set an
 * icon, which keeps it unambiguous: three different nodes are called 'Settings'
 * and only the top-level group carries an icon.
 *
 * Beyond branding, these swaps exist because Lucide draws at 2 units of a 24
 * grid and the Ochat glyphs sit between 0.87 and 1.31, so a Lucide icon next to
 * an Ochat one reads as noticeably heavier. Every entry that ends up next to an
 * Ochat icon gets one.
 */
export const OCHAT_ICONS = {
  Inbox: 'i-ochat-inbox',
  Conversation: 'i-ochat-conversations',
  // Las cuatro hijas de Conversaciones. 'All' reusa el icono de Bandeja de
  // entrada a pedido: son la misma idea vista desde dos lugares del menu.
  All: 'i-ochat-inbox',
  Mentions: 'i-ochat-mentions',
  Participating: 'i-ochat-participating',
  Unattended: 'i-ochat-unattended',
  // Llamadas es de primer nivel y solo aparece si el canal esta habilitado.
  Calls: 'i-ochat-calls',
  Channels: 'i-ochat-channels',
  Contacts: 'i-ochat-contacts',
  Settings: 'i-ochat-settings',
  'Settings Account Settings': 'i-ochat-account',
  'Settings Agents': 'i-ochat-agents',
  'Settings Labels': 'i-ochat-labels',
  'Settings Teams': 'i-ochat-teams',
  'Settings Canned Responses': 'i-ochat-canned',
  'Settings Macros': 'i-ochat-macros',
};

/** Builds the entries Ochat adds. Kept as a function so labels resolve per locale. */
const ochatItems = ({
  t,
  accountScopedRoute,
  hasBroadcasts,
  hasAgentAnalytics,
  hasBookings,
  permissions = [],
}) => [
  {
    after: 'Companies',
    // Administrators always see it. For agents it depends on the account flag: the
    // dashboard shows them a reduced, read-only board of their own conversations, and
    // whether the support team gets that is each account's decision.
    enabled:
      permissions.includes('administrator') ||
      permissions.includes(SUPER_ADMIN_PERMISSION) ||
      hasAgentAnalytics,
    item: {
      name: 'DashboardAnalytics',
      label: t('SIDEBAR.DASHBOARD_ANALYTICS'),
      icon: 'i-ochat-dashboard',
      to: accountScopedRoute('dashboard_analytics'),
      activeOn: ['dashboard_analytics'],
    },
  },
  {
    after: 'Campaigns',
    enabled: Boolean(hasBookings),
    item: {
      name: 'Bookings',
      label: t('SIDEBAR.BOOKINGS'),
      icon: 'i-ochat-bookings',
      children: [
        {
          name: 'BookingsCalendar',
          label: t('SIDEBAR.BOOKINGS_CALENDAR'),
          to: accountScopedRoute('bookings_calendar'),
        },
        {
          name: 'BookingsList',
          label: t('SIDEBAR.BOOKINGS_LIST'),
          to: accountScopedRoute('bookings_list'),
        },
        {
          name: 'BookingsNew',
          label: t('SIDEBAR.BOOKINGS_NEW'),
          to: accountScopedRoute('bookings_new'),
        },
        // Cupos y tarifas es configuración del hotel, no operación: va solo para
        // administradores de la cuenta. Es el mismo permiso que declara la ruta y
        // que exigen las policies del servidor, así el menú no ofrece algo que al
        // hacer clic redirige. No se usa `super_admin`: las policies miran el ROL
        // de la membresía, así que un super admin que en esta cuenta sea agente
        // vería la entrada y comería un 403. Ver bookings.routes.js.
        ...(permissions.includes('administrator')
          ? [
              {
                name: 'BookingsInventory',
                label: t('SIDEBAR.BOOKINGS_INVENTORY'),
                to: accountScopedRoute('bookings_inventory'),
              },
            ]
          : []),
      ],
    },
  },
  {
    after: 'Campaigns',
    enabled: Boolean(hasBroadcasts),
    item: {
      name: 'Marketing',
      label: t('SIDEBAR.MARKETING'),
      icon: 'i-ochat-marketing',
      children: [
        {
          name: 'NewCampaign',
          label: t('SIDEBAR.NEW_CAMPAIGN'),
          to: accountScopedRoute('marketing_new_campaign'),
        },
        {
          name: 'CampaignHistory',
          label: t('SIDEBAR.CAMPAIGN_HISTORY'),
          to: accountScopedRoute('marketing_history'),
        },
      ],
    },
  },
];

const withOchatIcon = item =>
  item.icon && OCHAT_ICONS[item.name]
    ? { ...item, icon: OCHAT_ICONS[item.name] }
    : item;

/**
 * Recursively drops the entries this viewer should not see and swaps icons.
 * Does not mutate the input.
 */
const applyVisibilityAndIcons = (items, viewer) =>
  items
    .filter(item => isMenuItemVisible(item.name, viewer))
    .map(item =>
      item.children
        ? {
            ...withOchatIcon(item),
            children: applyVisibilityAndIcons(item.children, viewer),
          }
        : withOchatIcon(item)
    );

/**
 * Inserts `item` right after the entry named `after`. Appends if the anchor is
 * gone, so an upstream rename degrades to a misplaced menu entry rather than a
 * missing one. specs/menuPolicy.spec.js asserts the anchors still exist.
 */
const insertAfter = (items, anchorName, item) => {
  const index = items.findIndex(candidate => candidate.name === anchorName);
  if (index === -1) return [...items, item];

  return [...items.slice(0, index + 1), item, ...items.slice(index + 1)];
};

/**
 * @param {Array} items upstream's menuItems array, untouched
 * @param {object} ctx
 * @param {Function} ctx.t translator
 * @param {Function} ctx.accountScopedRoute route builder
 * @param {boolean} ctx.hasBroadcasts whether the broadcasts feature flag is on
 * @param {boolean} ctx.hasBookings whether the bookings feature flag is on
 * @param {boolean} ctx.hasAgentAnalytics whether AGENTS may open the analytics
 *   dashboard (administrators always may)
 * @param {string[]} [ctx.permissions] the viewer's permissions for the active
 *   account, straight from AccountUser#permissions. Reading the published
 *   permissions rather than currentUser.type keeps the menu and the route guard
 *   on exactly the same input.
 * @returns {Array} the tree Ochat renders for this viewer
 */
export const applyOchatMenuPolicy = (items, ctx) => {
  const withInserts = ochatItems(ctx)
    .filter(({ enabled }) => enabled !== false)
    .reduce((acc, { after, item }) => insertAfter(acc, after, item), items);

  return applyVisibilityAndIcons(withInserts, {
    permissions: ctx.permissions ?? [],
  });
};

export default applyOchatMenuPolicy;
